/**
 * Custom Chunk Uploader für große Dateien
 * Orientiert an filepond's chunk-upload Implementierung
 */
export class ChunkUploader {
    constructor(uppy, opts) {
        this.uppy = uppy;
        this.id = 'ChunkUploader'; // ID für Uppy
        this.type = 'uploader';    // Typ für Uppy
        this.opts = Object.assign({
            endpoint: '',
            chunkSize: 5 * 1024 * 1024, // 5MB default
            categoryId: 0,
            uploadDir: '',
            apiToken: ''
        }, opts);
        
        this.uploaders = new Map();
    }

    install() {
        this.uppy.addUploader(this.uploadFile.bind(this));
    }

    uninstall() {
        // Cleanup
    }

    async uploadFile(fileIDs) {
        const promises = fileIDs.map(id => this.uploadSingleFile(id));
        return Promise.all(promises);
    }

    async uploadSingleFile(fileID) {
        const file = this.uppy.getFile(fileID);
        const chunkSize = this.opts.chunkSize;
        const totalSize = file.data.size;
        const totalChunks = Math.ceil(totalSize / chunkSize);
        
        // Set file state to uploading (wichtig für Dashboard UI)
        this.uppy.setFileState(fileID, {
            progress: {
                uploadStarted: Date.now(),
                uploadComplete: false,
                percentage: 0,
                bytesUploaded: 0,
                bytesTotal: totalSize
            }
        });
        
        try {
            // Prepare: Get fileId from server
            const fileId = await this.prepareUpload(file);
            
            this.uppy.emit('upload-progress', this.uppy.getFile(fileID), {
                uploader: this,
                bytesUploaded: 0,
                bytesTotal: totalSize
            });

            // Upload chunks sequentially
            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const start = chunkIndex * chunkSize;
                const end = Math.min(start + chunkSize, totalSize);
                const chunk = file.data.slice(start, end);
                
                await this.uploadChunk(fileId, chunk, chunkIndex, totalChunks, file);
                
                // Progress update
                this.uppy.emit('upload-progress', this.uppy.getFile(fileID), {
                    uploader: this,
                    bytesUploaded: end,
                    bytesTotal: totalSize
                });
            }

            // Finalize: Merge chunks and add to mediapool
            const result = await this.finalizeUpload(fileId, file, totalChunks);
            
            try {
                const currentFile = this.uppy.getFile(fileID);
                
                if (currentFile) {
                    // Mark file as complete
                    this.uppy.setFileState(fileID, {
                        progress: {
                            uploadComplete: true,
                            uploadStarted: currentFile.progress?.uploadStarted || Date.now(),
                            percentage: 100,
                            bytesUploaded: totalSize,
                            bytesTotal: totalSize
                        }
                    });
                    
                    // Emit success event
                    this.uppy.emit('upload-success', currentFile, result);
                }
            } catch (emitError) {
                console.error('Fehler beim Emitten von upload-success:', emitError);
            }
            
            return result;
        } catch (error) {
            console.error('ChunkUpload Fehler:', error);
            this.uppy.emit('upload-error', this.uppy.getFile(fileID), error);
            throw error;
        }
    }

    async prepareUpload(file) {
        // Prüfe ob fileId bereits existiert (z.B. aus Modal oder file-added Event)
        let fileId = file.meta.fileId;
        
        if (fileId) {
            // FileId existiert bereits - prüfe ob Metadaten aktualisiert werden müssen
            // Metadaten sammeln
            const metadata = {};
            Object.keys(file.meta).forEach(function(key) {
                // Filtere Uppy-interne Felder aus (inkl. fileId)
                if (!['name', 'type', 'size', 'relativePath', 'fileId'].includes(key)) {
                    metadata[key] = file.meta[key];
                }
            });
            
            // Wenn Metadaten vorhanden sind, aktualisiere sie
            if (Object.keys(metadata).length > 0) {
                const formData = new FormData();
                formData.append('fileId', fileId);
                formData.append('metadata', JSON.stringify(metadata));
                
                const url = `${this.opts.endpoint}&func=prepare`;
                
                try {
                    const response = await fetch(url, {
                        method: 'POST',
                        body: formData,
                        credentials: 'same-origin'
                    });
                    
                    if (!response.ok) {
                        console.warn('Metadata update failed:', response.status);
                    }
                } catch (error) {
                    console.warn('Metadata update error:', error);
                }
            }
            
            return fileId;
        }
        
        // Keine fileId vorhanden - neue generieren und prepare aufrufen
        fileId = 'uppy_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        
        // Metadaten sammeln (MUSS VOR console.log sein!)
        const metadata = {};
        Object.keys(file.meta).forEach(function(key) {
            // Filtere Uppy-interne Felder aus
            if (!['name', 'type', 'size', 'relativePath', 'fileId'].includes(key)) {
                metadata[key] = file.meta[key];
            }
        });
        
        const formData = new FormData();
        formData.append('fileId', fileId);
        formData.append('fileName', file.name);
        formData.append('fieldName', 'file');
        formData.append('metadata', JSON.stringify(metadata));
        
        // Endpoint URL enthält bereits Signatur-Parameter (siehe Constructor)
        const url = `${this.opts.endpoint}&func=prepare`;
        
        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        });
        
        if (!response.ok) {
            const text = await response.text();
            console.error('Prepare failed:', text);
            throw new Error('Prepare failed: ' + response.status);
        }
        
        const data = await response.json();
        
        // FileId in file.meta speichern
        this.uppy.setFileMeta(file.id, { fileId: data.fileId || fileId });
        
        return data.fileId || fileId;
    }

    async uploadChunk(fileId, chunk, chunkIndex, totalChunks, file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', chunk, file.name);
            formData.append('fileId', fileId);
            formData.append('chunkIndex', chunkIndex);
            formData.append('totalChunks', totalChunks);
            formData.append('fieldName', 'file');
            
            // Kategorie-ID dynamisch ermitteln falls es eine Funktion ist
            const categoryId = typeof this.opts.categoryId === 'function' 
                ? this.opts.categoryId() 
                : this.opts.categoryId;

            const uploadDir = typeof this.opts.uploadDir === 'function'
                ? this.opts.uploadDir()
                : this.opts.uploadDir;
            
            // Endpoint URL enthält bereits Signatur-Parameter
            const url = `${this.opts.endpoint}&func=chunk&category_id=${categoryId}&upload_dir=${encodeURIComponent(uploadDir)}`;
            
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);
            xhr.withCredentials = true;
            
            // Upload Progress für diesen Chunk
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const chunkSize = this.opts.chunkSize;
                    const start = chunkIndex * chunkSize;
                    const totalUploaded = start + e.loaded;
                    
                    // Progress an Uppy melden
                    // Wichtig: Immer das aktuellste File-Objekt holen
                    const currentFile = this.uppy.getFile(file.id);
                    if (currentFile) {
                        this.uppy.emit('upload-progress', currentFile, {
                            uploader: this,
                            bytesUploaded: totalUploaded,
                            bytesTotal: file.data.size
                        });
                    }
                }
            };
            
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        resolve(response);
                    } catch (err) {
                        console.error(`Chunk ${chunkIndex} JSON parse error:`, err);
                        reject(new Error(`Chunk ${chunkIndex} invalid response`));
                    }
                } else {
                    console.error(`Chunk ${chunkIndex} upload failed:`, xhr.statusText);
                    reject(new Error(`Chunk ${chunkIndex} upload failed: ` + xhr.status));
                }
            };
            
            xhr.onerror = () => reject(new Error(`Chunk ${chunkIndex} network error`));
            
            xhr.send(formData);
        });
    }

    async finalizeUpload(fileId, file, totalChunks) {
        const formData = new FormData();
        formData.append('fileId', fileId);
        formData.append('fileName', file.name);
        formData.append('totalChunks', totalChunks);
        formData.append('fieldName', 'file');
        
        // Metadaten mitsenden
        if (file.meta) {
            formData.append('metadata', JSON.stringify(file.meta));
        }
        
        // Kategorie-ID dynamisch ermitteln falls es eine Funktion ist
        const categoryId = typeof this.opts.categoryId === 'function' 
            ? this.opts.categoryId() 
            : this.opts.categoryId;

        const uploadDir = typeof this.opts.uploadDir === 'function'
            ? this.opts.uploadDir()
            : this.opts.uploadDir;
            
        // Endpoint URL enthält bereits Signatur-Parameter
        const url = `${this.opts.endpoint}&func=finalize&category_id=${categoryId}&upload_dir=${encodeURIComponent(uploadDir)}`;
        
        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        });
        
        if (!response.ok) {
            const text = await response.text();
            console.error('Finalize failed:', text);
            throw new Error('Finalize failed: ' + response.status);
        }
        
        const data = await response.json();
        
        // Prüfe ob die Response das erwartete Format hat
        if (!data.success && !data.data?.filename) {
            console.error('Unexpected finalize response format:', data);
            throw new Error('Invalid server response after finalize');
        }
        
        // Uppy erwartet ein Response-Objekt mit status und body
        return {
            status: response.status,
            body: {
                success: data.success !== false,
                data: {
                    filename: data.data?.filename || data.filename,
                    title: data.data?.title || ''
                }
            },
            uploadURL: ''
        };
    }
}